// frontend/src/pages/BusinessDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    BuildingOfficeIcon,
    MapPinIcon,
    GlobeAltIcon,
    UserIcon,
    PhoneIcon,
    EnvelopeIcon,
    DocumentTextIcon,
    ArrowLeftIcon,
    CheckCircleIcon,
    ClockIcon,
    PlusIcon
} from '@heroicons/react/24/outline';

const BusinessDetail = () => {
    const { businessId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [business, setBusiness] = useState(null);
    const [showInteractionModal, setShowInteractionModal] = useState(false);
    const [interactionForm, setInteractionForm] = useState({
        interaction_type: 'meeting',
        summary: '',
        duration_minutes: 30,
        date: new Date().toISOString().slice(0, 16)
    });

    useEffect(() => {
        loadBusinessDetails();
    }, [businessId]);

    const loadBusinessDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/business/${businessId}`);
            setBusiness(response.data.data);
        } catch (error) {
            toast.error('Failed to load business details');
            navigate('/business');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateInteraction = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/business/interaction', {
                ...interactionForm,
                business_id: parseInt(businessId)
            });
            toast.success('Interaction logged successfully');
            setShowInteractionModal(false);
            setInteractionForm({
                interaction_type: 'meeting',
                summary: '',
                duration_minutes: 30,
                date: new Date().toISOString().slice(0, 16)
            });
            loadBusinessDetails();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to log interaction');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading business details...</div>
            </div>
        );
    }

    if (!business) return null;

    return (
        <div className="w-full space-y-6">
            {/* Navigation back */}
            <Link to="/business" className="inline-flex items-center text-sm text-gray-600 hover:text-black space-x-1">
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to My Businesses</span>
            </Link>

            {/* Business Header Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex justify-between items-start flex-wrap gap-4">
                    <div>
                        <div className="flex items-center space-x-3">
                            <BuildingOfficeIcon className="w-8 h-8 text-black" />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">{business.name}</h1>
                                <p className="text-sm text-gray-500">{business.industry || 'Unspecified Industry'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-black text-white capitalize">
                            {business.status}
                        </span>
                        <Link
                            to={`/business/${business.id}/assessment`}
                            className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors"
                        >
                            {business.assessment ? 'Edit Assessment' : 'Start Assessment'}
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <MapPinIcon className="w-5 h-5 text-gray-400" />
                        <span>{business.location || business.city || 'No location provided'}</span>
                    </div>
                    {business.website && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                            <a href={business.website} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600">
                                {business.website}
                            </a>
                        </div>
                    )}
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <UserIcon className="w-5 h-5 text-gray-400" />
                        <span>Contact: {business.contact_name || 'N/A'}</span>
                    </div>
                </div>
            </div>

            {/* Overview & Contact Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900">About Business</h3>
                    <p className="text-sm text-gray-600">{business.description || 'No description added yet.'}</p>
                    <div className="pt-2 text-xs text-gray-500 space-y-1">
                        <div><strong>Employees:</strong> {business.employees || 'Not specified'}</div>
                        <div><strong>Consent Obtained:</strong> {business.consent_obtained ? '✅ Yes' : '❌ Pending'}</div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                            <UserIcon className="w-4 h-4 text-gray-400" />
                            <span>{business.contact_name || 'N/A'} {business.contact_role ? `(${business.contact_role})` : ''}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                            <span>{business.contact_email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <PhoneIcon className="w-4 h-4 text-gray-400" />
                            <span>{business.contact_phone || 'N/A'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Assessment Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Business Assessment</h3>
                    <Link
                        to={`/business/${business.id}/assessment`}
                        className="text-sm font-medium text-black hover:underline"
                    >
                        {business.assessment ? 'View/Update Assessment →' : '+ Create Assessment'}
                    </Link>
                </div>

                {business.assessment ? (
                    <div className="bg-gray-50 p-4 rounded-md space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="font-medium text-gray-900">{business.assessment.title || 'General Assessment'}</span>
                            <span className="text-xs bg-white border border-gray-300 px-2 py-0.5 rounded capitalize">{business.assessment.status}</span>
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-2">{business.assessment.business_overview || 'No overview provided.'}</p>
                        {business.assessment.mentor_feedback && (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 text-xs text-yellow-800 rounded">
                                <strong>Mentor Feedback:</strong> {business.assessment.mentor_feedback}
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No assessment completed yet for this business.</p>
                )}
            </div>

            {/* Interactions Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Interaction History</h3>
                    <button
                        onClick={() => setShowInteractionModal(true)}
                        className="px-3 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 flex items-center space-x-1"
                    >
                        <PlusIcon className="w-4 h-4" />
                        <span>Log Interaction</span>
                    </button>
                </div>

                {business.interactions && business.interactions.length > 0 ? (
                    <div className="space-y-3">
                        {business.interactions.map((interaction) => (
                            <div key={interaction.id} className="p-3 border border-gray-100 rounded-md bg-gray-50 flex justify-between items-start">
                                <div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs px-2 py-0.5 bg-black text-white rounded capitalize">{interaction.interaction_type}</span>
                                        <span className="text-xs text-gray-500">{new Date(interaction.date).toLocaleString()}</span>
                                    </div>
                                    <p className="text-sm text-gray-700 mt-1">{interaction.summary}</p>
                                </div>
                                <span className="text-xs text-gray-400">{interaction.duration_minutes} mins</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No interactions recorded yet.</p>
                )}
            </div>

            {/* Log Interaction Modal */}
            {showInteractionModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Log Interaction</h3>
                        <form onSubmit={handleCreateInteraction} className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700">Type</label>
                                <select
                                    value={interactionForm.interaction_type}
                                    onChange={(e) => setInteractionForm({ ...interactionForm, interaction_type: e.target.value })}
                                    className="mt-1 block w-full text-sm border border-gray-300 rounded p-2"
                                >
                                    <option value="call">Call</option>
                                    <option value="meeting">Meeting</option>
                                    <option value="email">Email</option>
                                    <option value="site_visit">Site Visit</option>
                                    <option value="presentation">Presentation</option>
                                    <option value="demo">Demo</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700">Duration (Minutes)</label>
                                <input
                                    type="number"
                                    value={interactionForm.duration_minutes}
                                    onChange={(e) => setInteractionForm({ ...interactionForm, duration_minutes: e.target.value })}
                                    className="mt-1 block w-full text-sm border border-gray-300 rounded p-2"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700">Summary</label>
                                <textarea
                                    required
                                    rows={3}
                                    value={interactionForm.summary}
                                    onChange={(e) => setInteractionForm({ ...interactionForm, summary: e.target.value })}
                                    className="mt-1 block w-full text-sm border border-gray-300 rounded p-2"
                                    placeholder="Notes on the conversation..."
                                />
                            </div>

                            <div className="flex justify-end space-x-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowInteractionModal(false)}
                                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-1.5 text-sm bg-black text-white rounded hover:bg-gray-800"
                                >
                                    Save Log
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusinessDetail;
